import { createSlider } from '@theme/coco-utilities';
import { onCocoEvent, COCO_EVENTS } from '@theme/theme-events';

const MEDIA_ITEM_SELECTOR = '[data-product-media-item][data-media-id]';
const VISIBLE_MEDIA_ITEM_SELECTOR = '[data-product-media-item][data-media-id]:not([hidden])';

if (!customElements.get('product-info')) {
  class MainProduct extends HTMLElement {
    connectedCallback() {
      this.initGallery();
      this.querySelector('[data-fbt-add-all]')?.addEventListener('click', this.onFbtAddAll.bind(this));

      this.onVariantChangeUnsubscriber = onCocoEvent(COCO_EVENTS.variantChanged, (detail) => {
        if (!detail) return;
        if (String(detail.sectionId) !== String(this.dataset.sectionId)) return;
        if (detail.productId && String(detail.productId) !== String(this.dataset.productId)) return;

        const variant = detail.variant;
        if (!variant?.id) return;

        const featuredMediaId =
          variant.featured_media?.id ||
          variant.featured_image?.id ||
          this.getFeaturedMediaIdForVariant(variant.id);

        if (!featuredMediaId) return;

        if (this.isMediaGridLayout()) {
          this.renderGridMedia(featuredMediaId, { scrollPage: false });
        } else {
          this.focusGalleryMedia(featuredMediaId, 0, { scrollPage: false });
        }
      });

      this.onProductInfoLoaded = (event) => {
        const { sectionId, variant } = event.detail || {};
        if (String(sectionId) !== String(this.dataset.sectionId)) return;

        this.destroyGallery();
        this.initGallery();

        const featuredMediaId =
          variant?.featured_media?.id ||
          variant?.featured_image?.id ||
          this.getFeaturedMediaIdForVariant(variant?.id);

        if (featuredMediaId) {
          if (this.isMediaGridLayout()) {
            this.renderGridMedia(featuredMediaId, { scrollPage: false });
          } else {
            this.focusGalleryMedia(featuredMediaId, 0, { scrollPage: false });
          }
        }
      };
      document.addEventListener('productInfo:loaded', this.onProductInfoLoaded);
      this.bindDesignModeEvents();
    }

    get isShopifyDesignMode() {
      return typeof Shopify !== 'undefined' && Shopify.designMode;
    }

    bindDesignModeEvents() {
      if (!this.isShopifyDesignMode || this._designModeBound) return;
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this._designModeBound = true;
    }

    unbindDesignModeEvents() {
      if (!this._designModeBound) return;
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this._designModeBound = false;
    }

    onBlockSelect(event) {
      const blockEl = event.target.closest('.product-block');
      if (!blockEl || !this.contains(blockEl)) return;
      blockEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    disconnectedCallback() {
      this.unbindDesignModeEvents();
      this.onVariantChangeUnsubscriber?.();
      if (this.onProductInfoLoaded) {
        document.removeEventListener('productInfo:loaded', this.onProductInfoLoaded);
      }
      this.destroyGallery();
    }

    getGalleryRoot() {
      return this.querySelector('[data-product-gallery]');
    }

    isMediaGridLayout() {
      const gallery = this.querySelector('product-gallery[data-media-grid]');
      return Boolean(gallery) && window.matchMedia('(min-width: 768px)').matches;
    }

    getGalleryElements() {
      return {
        elementMain: this.querySelector('[data-product-gallery-main]'),
        elementThumb: this.querySelector('[data-product-gallery-thumbs]'),
        galleryRoot: this.getGalleryRoot(),
      };
    }

    getMediaItems(root = this.getGalleryRoot()) {
      return root ? [...root.querySelectorAll(MEDIA_ITEM_SELECTOR)] : [];
    }

    getVisibleMediaItems(root = this.getGalleryRoot()) {
      return root ? [...root.querySelectorAll(VISIBLE_MEDIA_ITEM_SELECTOR)] : [];
    }

    setMediaItemVisible(item, visible) {
      item.toggleAttribute('hidden', !visible);
    }

    isSwiperDuplicate(slide) {
      return Boolean(slide?.swiperSlideDuplicate);
    }

    destroyGallery() {
      if (this.mainSwiper && this._onGallerySlideChange) {
        this.mainSwiper.off('slideChangeTransitionEnd', this._onGallerySlideChange);
        this._onGallerySlideChange = null;
      }
      if (this.mainSwiper?.destroy) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }
      if (this.thumbSwiper?.destroy) {
        this.thumbSwiper.destroy(true, true);
        this.thumbSwiper = null;
      }
    }

    refreshGallery() {
      const { elementMain } = this.getGalleryElements();
      if (!elementMain) return;

      if (!this.mainSwiper || this.mainSwiper.destroyed || this.mainSwiper.width === 0) {
        this.destroyGallery();
        this.initGallery();
        return;
      }

      this.mainSwiper.update();
      this.thumbSwiper?.update?.();
    }

    async initGallery() {
      const { elementMain, elementThumb } = this.getGalleryElements();
      if (!elementMain) return;

      this.destroyGallery();

      const swiperThumbs = elementThumb
        ? await createSlider(elementThumb, { bypassLcpGate: true })
        : null;
      const visibleSlides = elementMain.querySelectorAll(VISIBLE_MEDIA_ITEM_SELECTOR);
      const slideCount =
        visibleSlides.length || elementMain.querySelectorAll(MEDIA_ITEM_SELECTOR).length;

      this.mainSwiper = await createSlider(elementMain, {
        loop: slideCount > 1,
        initialSlide: this.getInitialSlideIndex(elementMain),
        thumbs: swiperThumbs ? { swiper: swiperThumbs } : undefined,
        bypassLcpGate: true,
      });
      this.thumbSwiper = swiperThumbs;
      this.bindGalleryVideoPlayback();
    }

    bindGalleryVideoPlayback() {
      if (!this.mainSwiper) return;

      if (this._onGallerySlideChange) {
        this.mainSwiper.off('slideChangeTransitionEnd', this._onGallerySlideChange);
      }

      this._onGallerySlideChange = () => this.syncGalleryVideoPlayback();
      this.mainSwiper.on('slideChangeTransitionEnd', this._onGallerySlideChange);
      this.syncGalleryVideoPlayback();
    }

    syncGalleryVideoPlayback() {
      const { galleryRoot } = this.getGalleryElements();
      if (!galleryRoot || !this.mainSwiper) return;

      const activeSlide = this.mainSwiper.slides?.[this.mainSwiper.activeIndex];
      if (!activeSlide) return;

      const isInViewport = (element) => {
        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return false;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        return (
          rect.top < windowHeight &&
          rect.bottom > 0 &&
          rect.left < windowWidth &&
          rect.right > 0
        );
      };

      galleryRoot.querySelectorAll('video-media').forEach((videoMedia) => {
        const slide = videoMedia.closest('.swiper-slide');
        if (!slide || this.isSwiperDuplicate(slide)) return;

        if (slide !== activeSlide || !isInViewport(videoMedia)) {
          videoMedia.pause();
          return;
        }

        if (videoMedia.autoplay) {
          videoMedia.paused = false;
          videoMedia._viewportIntersecting = true;
          videoMedia.play();
        }
      });
    }

    getCurrentVariantId() {
      return (
        this.querySelector('[name="id"]')?.value ||
        new URLSearchParams(window.location.search).get('variant')
      );
    }

    getInitialSlideIndex(elementMain) {
      const fromMarkup = Number.parseInt(elementMain?.dataset.initialSlide, 10);
      if (!Number.isNaN(fromMarkup) && fromMarkup >= 0) return fromMarkup;

      const variantId = this.getCurrentVariantId();
      if (!variantId || !this.mediaMap) return 0;

      const featuredMediaId = this.getFeaturedMediaIdForVariant(variantId);
      if (!featuredMediaId) return 0;

      const slide = elementMain?.querySelector(
        `${VISIBLE_MEDIA_ITEM_SELECTOR}[data-media-id="${featuredMediaId}"]`,
      );
      if (!slide) {
        return (
          elementMain?.querySelector(`${MEDIA_ITEM_SELECTOR}[data-media-id="${featuredMediaId}"]`)
            ?.dataset.mediaIndex ?? 0
        );
      }

      return Number(slide.dataset.mediaIndex) || 0;
    }

    getFeaturedMediaIdForVariant(variantId) {
      return (
        this.mediaMap?.variants?.find((entry) => String(entry.id) === String(variantId))
          ?.featuredMediaId || null
      );
    }

    getSlideIndexForMediaId(mediaId) {
      if (!mediaId) return null;

      const { elementMain } = this.getGalleryElements();
      const slides = this.mainSwiper?.slides || elementMain?.querySelectorAll(MEDIA_ITEM_SELECTOR) || [];

      for (let index = 0; index < slides.length; index += 1) {
        const slide = slides[index];
        if (this.isSwiperDuplicate(slide)) continue;
        if (!slide.dataset?.mediaId || slide.hidden) continue;
        if (String(slide.dataset.mediaId) === String(mediaId)) return index;
      }

      return this.mediaIndex?.get(String(mediaId)) ?? null;
    }

    slideToMediaId(mediaId, speed = 0) {
      if (!mediaId || !this.mainSwiper) return;

      const index = this.getSlideIndexForMediaId(mediaId);
      if (index == null) return;

      if (this.mainSwiper.params?.loop) {
        this.mainSwiper.slideToLoop(index, speed);
      } else {
        this.mainSwiper.slideTo(index, speed);
      }

      if (this.thumbSwiper) {
        if (this.thumbSwiper.params?.loop) {
          this.thumbSwiper.slideToLoop(index, speed);
        } else {
          this.thumbSwiper.slideTo(index, speed);
        }
      }
    }

    scrollGalleryToMedia(mediaId) {
      const mediaEl = this.querySelector(
        `[data-product-gallery] ${VISIBLE_MEDIA_ITEM_SELECTOR}[data-media-id="${mediaId}"]`,
      );
      if (!mediaEl) return;

      if (window.lenis?.scrollTo) {
        window.lenis.scrollTo(mediaEl, { offset: 0 });
        return;
      }

      mediaEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    focusGalleryMedia(mediaId, speed = 0, { scrollPage = false } = {}) {
      if (!mediaId) return;

      if (this.mainSwiper && !this.mainSwiper.destroyed) {
        this.slideToMediaId(mediaId, speed);
        return;
      }

      if (scrollPage) this.scrollGalleryToMedia(mediaId);
    }

    renderGridMedia(mediaId, { scrollPage = false } = {}) {
      const { galleryRoot } = this.getGalleryElements();
      if (!galleryRoot) return;

      const visibleItems = this.getVisibleMediaItems(galleryRoot);
      const fallbackItem = visibleItems[0];
      const target =
        visibleItems.find((item) => String(item.dataset.mediaId) === String(mediaId)) || fallbackItem;

      visibleItems.forEach((item) => {
        item.classList.toggle('is-active', item === target);
      });

      if (!target || !scrollPage) return;

      if (window.lenis?.scrollTo) {
        window.lenis.scrollTo(target, { offset: 0 });
        return;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    getFeaturedMediaId(variant) {
      return (
        variant?.featured_media?.id ||
        variant?.featured_image?.id ||
        variant?.featured_media_id ||
        null
      );
    }

    isMediaVisibleForVariant(mediaId, variantId) {
      const linkedVariants =
        this.mediaMap?.variants?.filter(
          (entry) => entry.featuredMediaId && String(entry.featuredMediaId) === String(mediaId),
        ) || [];

      if (!linkedVariants.length) return true;
      return linkedVariants.some((entry) => String(entry.id) === String(variantId));
    }

    filterMediaByVariant(variantId) {
      const { galleryRoot } = this.getGalleryElements();

      this.getMediaItems(galleryRoot).forEach((item) => {
        this.setMediaItemVisible(item, this.isMediaVisibleForVariant(item.dataset.mediaId, variantId));
      });

      const visibleCount = this.getVisibleMediaItems(galleryRoot).length;
      if (this.mainSwiper?.params) {
        this.mainSwiper.params.loop = visibleCount > 1;
      }

      this.mainSwiper?.update?.();
      this.thumbSwiper?.update?.();

      const featuredMediaId = this.mediaMap?.variants?.find(
        (entry) => String(entry.id) === String(variantId),
      )?.featuredMediaId;

      if (featuredMediaId && this.isMediaGridLayout()) {
        this.renderGridMedia(featuredMediaId);
      }
    }

    async onFbtAddAll(event) {
      const root = event.currentTarget.closest('[data-fbt]');
      if (!root) return;

      const errorEl = root.querySelector('[data-fbt-error]');
      const button = event.currentTarget;
      const items = [...root.querySelectorAll('[data-fbt-item]:checked')].map((input) => ({
        id: input.value,
        quantity: 1,
      }));

      if (!items.length) {
        if (errorEl) {
          errorEl.textContent = 'Select at least one product.';
          errorEl.hidden = false;
        }
        return;
      }

      if (errorEl) errorEl.hidden = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');

      try {
        const response = await fetch(`${window.Coco?.routes?.cartAdd || '/cart/add'}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) throw new Error('Add to cart failed');
        window.location.href = window.Coco?.routes?.cart || '/cart';
      } catch (error) {
        console.error(error);
        if (errorEl) {
          errorEl.textContent = window.Coco?.cartStrings?.error || 'Could not add items to cart.';
          errorEl.hidden = false;
        }
      } finally {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
      }
    }
  }

  customElements.define('product-info', MainProduct);
}
